import React, { useState, useEffect } from 'react';
import { BarChart3, Users, FileText, MessageSquare, LogIn, Calendar, Filter, Download, Eye, AlertCircle, TrendingUp, Clock, Activity } from 'lucide-react';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState('week');
  
  // Data states
  const [activityStats, setActivityStats] = useState(null);
  const [auditLog, setAuditLog] = useState({ entries: [], totalCount: 0 });
  const [usersActivity, setUsersActivity] = useState([]);
  const [mostViewedFiles, setMostViewedFiles] = useState([]);
  const [loginActivity, setLoginActivity] = useState(null);
  const [commentsActivity, setCommentsActivity] = useState(null);
  const [securityActivity, setSecurityActivity] = useState([]);
  const [securityStats, setSecurityStats] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    userId: '',
    activityType: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 50
  });

  useEffect(() => {
    if (activeTab === 'overview') {
      loadOverviewData();
    } else if (activeTab === 'audit') {
      loadAuditLog();
    } else if (activeTab === 'users') {
      loadUsersActivity();
    } else if (activeTab === 'files') {
      loadMostViewedFiles();
    } else if (activeTab === 'logins') {
      loadLoginActivity();
    } else if (activeTab === 'comments') {
      loadCommentsActivity();
    } else if (activeTab === 'security') {
      loadSecurityActivity();
    }
  }, [activeTab, timeframe, filters]);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/reports/activity-stats?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActivityStats(data.stats);
      } else {
        setError('Hiba történt az áttekintő adatok betöltése során');
      }
    } catch (error) {
      console.error('Error loading overview data:', error);
      setError('Hiba történt az áttekintő adatok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLog = async () => {
    try {
      setLoading(true);
      setError('');
      
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await fetch(`/api/reports/audit-log?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditLog(data);
      } else {
        setError('Hiba történt az audit napló betöltése során');
      }
    } catch (error) {
      console.error('Error loading audit log:', error);
      setError('Hiba történt az audit napló betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const loadUsersActivity = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/reports/users-activity-summary?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsersActivity(data.users);
      } else {
        setError('Hiba történt a felhasználói aktivitás betöltése során');
      }
    } catch (error) {
      console.error('Error loading users activity:', error);
      setError('Hiba történt a felhasználói aktivitás betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const loadMostViewedFiles = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/reports/most-viewed-files?timeframe=${timeframe}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMostViewedFiles(data.files);
      } else {
        setError('Hiba történt a legnézettebb fájlok betöltése során');
      }
    } catch (error) {
      console.error('Error loading most viewed files:', error);
      setError('Hiba történt a legnézettebb fájlok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const loadLoginActivity = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/reports/login-activity?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLoginActivity(data);
      } else {
        setError('Hiba történt a bejelentkezési aktivitás betöltése során');
      }
    } catch (error) {
      console.error('Error loading login activity:', error);
      setError('Hiba történt a bejelentkezési aktivitás betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const loadCommentsActivity = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`/api/reports/comments-activity?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCommentsActivity(data);
      } else {
        setError('Hiba történt a megjegyzés aktivitás betöltése során');
      }
    } catch (error) {
      console.error('Error loading comments activity:', error);
      setError('Hiba történt a megjegyzés aktivitás betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const loadSecurityActivity = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load security activities
      const activitiesResponse = await fetch(`/api/security/suspicious-activities?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Load security statistics
      const statsResponse = await fetch(`/api/security/statistics?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (activitiesResponse.ok && statsResponse.ok) {
        const activitiesData = await activitiesResponse.json();
        const statsData = await statsResponse.json();
        
        setSecurityActivity(activitiesData.activities);
        setSecurityStats(statsData.statistics);
      } else {
        setError('Hiba történt a biztonsági adatok betöltése során');
      }
    } catch (error) {
      console.error('Error loading security activity:', error);
      setError('Hiba történt a biztonsági adatok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAuditLog = async () => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'page' && key !== 'limit') {
          queryParams.append(key, value);
        }
      });
      
      const response = await fetch(`/api/reports/export-audit-log?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `audit_log_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        setError('Hiba történt az exportálás során');
      }
    } catch (error) {
      console.error('Error exporting audit log:', error);
      setError('Hiba történt az exportálás során');
    }
  };

  const getActivityTypeIcon = (type) => {
    switch (type) {
      case 'login': return <LogIn className="h-4 w-4" />;
      case 'pdf_view': return <Eye className="h-4 w-4" />;
      case 'comment_add': return <MessageSquare className="h-4 w-4" />;
      case 'file_upload': return <FileText className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getActivityTypeLabel = (type) => {
    const labels = {
      'login': 'Bejelentkezés',
      'logout': 'Kijelentkezés',
      'login_failed': 'Sikertelen bejelentkezés',
      'pdf_view': 'PDF megtekintés',
      'comment_add': 'Megjegyzés hozzáadása',
      'comment_edit': 'Megjegyzés szerkesztése',
      'comment_reply': 'Válasz megjegyzésre',
      'comment_delete': 'Megjegyzés törlése',
      'user_create': 'Felhasználó létrehozása',
      'user_update': 'Felhasználó módosítása',
      'user_delete': 'Felhasználó törlése',
      'file_upload': 'Fájl feltöltés',
      'file_delete': 'Fájl törlés',
      'folder_create': 'Mappa létrehozása',
      'permission_set': 'Jogosultság beállítása'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('hu-HU');
  };

  const tabs = [
    { id: 'overview', name: 'Áttekintés', icon: BarChart3 },
    { id: 'audit', name: 'Audit Napló', icon: Activity },
    { id: 'users', name: 'Felhasználói Aktivitás', icon: Users },
    { id: 'files', name: 'Fájl Statisztikák', icon: FileText },
    { id: 'logins', name: 'Bejelentkezések', icon: LogIn },
    { id: 'comments', name: 'Megjegyzések', icon: MessageSquare },
    { id: 'security', name: 'Biztonsági Riasztások', icon: AlertCircle }
  ];

  const timeframeOptions = [
    { value: 'day', label: 'Utolsó 24 óra' },
    { value: 'week', label: 'Utolsó hét' },
    { value: 'month', label: 'Utolsó hónap' },
    { value: 'year', label: 'Utolsó év' }
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Riportok és Statisztikák</h2>
        <p className="text-gray-600">Felhasználói aktivitás és rendszer használat elemzése</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Timeframe Selector */}
      <div className="mb-6 flex items-center space-x-4">
        <label className="block text-sm font-medium text-gray-700">
          Időszak:
        </label>
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {timeframeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Tab Content */}
      {!loading && (
        <div className="bg-white shadow rounded-lg">
          {/* Overview Tab */}
          {activeTab === 'overview' && activityStats && (
            <div className="p-6">
              <h3 className="text-lg font-medium mb-6">Aktivitás Áttekintés - {timeframeOptions.find(t => t.value === timeframe)?.label}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-blue-500" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Összes Aktivitás</p>
                      <p className="text-2xl font-semibold text-gray-900">{activityStats.totalActivities}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Users className="h-8 w-8 text-green-500" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Aktív Felhasználók</p>
                      <p className="text-2xl font-semibold text-gray-900">{activityStats.uniqueUsers}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Eye className="h-8 w-8 text-purple-500" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">PDF Megtekintések</p>
                      <p className="text-2xl font-semibold text-gray-900">{activityStats.activityCounts.pdf_view || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <MessageSquare className="h-8 w-8 text-orange-500" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-500">Megjegyzések</p>
                      <p className="text-2xl font-semibold text-gray-900">{activityStats.activityCounts.comment_add || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Types Chart */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium mb-4">Aktivitás Típusok</h4>
                  <div className="space-y-3">
                    {Object.entries(activityStats.activityCounts)
                      .filter(([, count]) => count > 0)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 8)
                      .map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center">
                            {getActivityTypeIcon(type)}
                            <span className="ml-2 text-sm text-gray-700">{getActivityTypeLabel(type)}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{count}</span>
                        </div>
                      ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium mb-4">Napi Aktivitás</h4>
                  <div className="space-y-2">
                    {Object.entries(activityStats.dailyCounts)
                      .sort(([a], [b]) => new Date(b) - new Date(a))
                      .slice(0, 7)
                      .map(([date, count]) => (
                        <div key={date} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-700">{new Date(date).toLocaleDateString('hu-HU')}</span>
                          <span className="text-sm font-semibold text-gray-900">{count} aktivitás</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium">Audit Napló</h3>
                <button
                  onClick={handleExportAuditLog}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportálás (CSV)
                </button>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Felhasználó ID</label>
                  <input
                    type="number"
                    value={filters.userId}
                    onChange={(e) => setFilters({...filters, userId: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Összes"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aktivitás típus</label>
                  <select
                    value={filters.activityType}
                    onChange={(e) => setFilters({...filters, activityType: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Összes</option>
                    <option value="login">Bejelentkezés</option>
                    <option value="pdf_view">PDF megtekintés</option>
                    <option value="comment_add">Megjegyzés</option>
                    <option value="file_upload">Fájl feltöltés</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kezdő dátum</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Befejező dátum</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>

              {/* Audit Log Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Időpont</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Felhasználó</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktivitás</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Állapot</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Cím</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Részletek</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditLog.entries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(entry.timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.username || `User ${entry.userId}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getActivityTypeIcon(entry.activityType)}
                            <span className="ml-2 text-sm text-gray-900">
                              {getActivityTypeLabel(entry.activityType)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            entry.success 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {entry.success ? 'Sikeres' : 'Sikertelen'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.details?.ipAddress || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {entry.details?.fileName || entry.details?.folderPath || entry.details?.url || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Összesen {auditLog.totalCount} bejegyzés, oldal {auditLog.page} / {auditLog.totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setFilters({...filters, page: Math.max(1, filters.page - 1)})}
                    disabled={filters.page <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                  >
                    Előző
                  </button>
                  <button
                    onClick={() => setFilters({...filters, page: filters.page + 1})}
                    disabled={filters.page >= auditLog.totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                  >
                    Következő
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Users Activity Tab */}
          {activeTab === 'users' && (
            <div className="p-6">
              <h3 className="text-lg font-medium mb-6">Felhasználói Aktivitás - {timeframeOptions.find(t => t.value === timeframe)?.label}</h3>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Felhasználó</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Szerepkör</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utolsó Bejelentkezés</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Összes Aktivitás</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PDF Megtekintések</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Megjegyzések</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usersActivity.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.role}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.activitySummary.lastLogin 
                            ? formatDate(user.activitySummary.lastLogin)
                            : 'Soha'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.activitySummary.totalActivities}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.activitySummary.pdfViewCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.activitySummary.commentCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Most Viewed Files Tab */}
          {activeTab === 'files' && (
            <div className="p-6">
              <h3 className="text-lg font-medium mb-6">Legnézettebb Fájlok - {timeframeOptions.find(t => t.value === timeframe)?.label}</h3>
              
              <div className="space-y-3">
                {mostViewedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.fileName}</p>
                        <p className="text-xs text-gray-500">Utoljára megtekintve: {formatDate(file.lastViewed)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{file.viewCount} megtekintés</p>
                      <p className="text-xs text-gray-500">{file.uniqueViewers} egyedi felhasználó</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Login Activity Tab */}
          {activeTab === 'logins' && loginActivity && (
            <div className="p-6">
              <h3 className="text-lg font-medium mb-6">Bejelentkezési Aktivitás - {timeframeOptions.find(t => t.value === timeframe)?.label}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500">Sikeres Bejelentkezések</p>
                  <p className="text-2xl font-semibold text-green-600">{loginActivity.successfulLogins}</p>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500">Sikertelen Bejelentkezések</p>
                  <p className="text-2xl font-semibold text-red-600">{loginActivity.failedLogins}</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500">Egyedi Felhasználók</p>
                  <p className="text-2xl font-semibold text-blue-600">{loginActivity.uniqueUsers}</p>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500">Egyedi IP Címek</p>
                  <p className="text-2xl font-semibold text-purple-600">{loginActivity.uniqueIpAddresses}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium mb-4">Legutóbbi Sikeres Bejelentkezések</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {loginActivity.recentLogins.slice(0, 10).map((login, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{login.username}</span>
                          <span className="text-gray-500">{formatDate(login.timestamp)}</span>
                        </div>
                        <div className="text-gray-500 text-xs">
                          IP: {login.details?.ipAddress || 'N/A'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium mb-4">Sikertelen Bejelentkezési Kísérletek</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {loginActivity.recentFailedLogins.slice(0, 10).map((attempt, index) => (
                      <div key={index} className="p-3 bg-red-50 rounded text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{attempt.username || 'Ismeretlen'}</span>
                          <span className="text-gray-500">{formatDate(attempt.timestamp)}</span>
                        </div>
                        <div className="text-gray-500 text-xs">
                          IP: {attempt.details?.ipAddress || 'N/A'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Comments Activity Tab */}
          {activeTab === 'comments' && commentsActivity && (
            <div className="p-6">
              <h3 className="text-lg font-medium mb-6">Megjegyzés Aktivitás - {timeframeOptions.find(t => t.value === timeframe)?.label}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500">Új Megjegyzések</p>
                  <p className="text-2xl font-semibold text-blue-600">{commentsActivity.commentsByType.comment_add || 0}</p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500">Admin Válaszok</p>
                  <p className="text-2xl font-semibold text-green-600">{commentsActivity.commentsByType.comment_reply || 0}</p>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500">Szerkesztések</p>
                  <p className="text-2xl font-semibold text-yellow-600">{commentsActivity.commentsByType.comment_edit || 0}</p>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500">Törlések</p>
                  <p className="text-2xl font-semibold text-red-600">{commentsActivity.commentsByType.comment_delete || 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium mb-4">Legaktívabb Kommentelők</h4>
                  <div className="space-y-2">
                    {commentsActivity.topCommenters.slice(0, 5).map((commenter, index) => (
                      <div key={index} className="flex justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">User {commenter.userId}</span>
                        <span className="text-sm font-semibold text-gray-900">{commenter.count} megjegyzés</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium mb-4">Legaktívabb Mappák</h4>
                  <div className="space-y-2">
                    {commentsActivity.topFolders.slice(0, 5).map((folder, index) => (
                      <div key={index} className="flex justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700 truncate">{folder.folderPath || 'Gyökér'}</span>
                        <span className="text-sm font-semibold text-gray-900">{folder.count} megjegyzés</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Alerts Tab */}
          {activeTab === 'security' && (
            <div className="p-6">
              <h3 className="text-lg font-medium mb-6">Biztonsági Riasztások - {timeframeOptions.find(t => t.value === timeframe)?.label}</h3>
              
              {securityStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">Összes Gyanús Tevékenység</p>
                    <p className="text-2xl font-semibold text-red-600">{securityStats.totalActivities}</p>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">Magas Kockázat</p>
                    <p className="text-2xl font-semibold text-orange-600">{securityStats.riskDistribution.high}</p>
                  </div>
                  
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">Közepes Kockázat</p>
                    <p className="text-2xl font-semibold text-yellow-600">{securityStats.riskDistribution.medium}</p>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">Átlagos Kockázati Szint</p>
                    <p className="text-2xl font-semibold text-blue-600">{securityStats.averageRiskLevel.toFixed(1)}/10</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {securityStats && (
                  <>
                    <div>
                      <h4 className="text-md font-medium mb-4">Tevékenység Típusok</h4>
                      <div className="space-y-2">
                        {Object.entries(securityStats.activityCounts)
                          .filter(([, count]) => count > 0)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 8)
                          .map(([type, count]) => (
                            <div key={type} className="flex justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm text-gray-700 capitalize">{type.replace(/_/g, ' ')}</span>
                              <span className="text-sm font-semibold text-gray-900">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-md font-medium mb-4">Gyanús Felhasználók</h4>
                      <div className="space-y-2">
                        {Object.entries(securityStats.userCounts)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 5)
                          .map(([userId, count]) => (
                            <div key={userId} className="flex justify-between p-2 bg-red-50 rounded">
                              <span className="text-sm text-gray-700">User {userId}</span>
                              <span className="text-sm font-semibold text-red-900">{count} esemény</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Recent Security Activities */}
              <div>
                <h4 className="text-md font-medium mb-4">Legfrissebb Biztonsági Események</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Időpont</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Felhasználó</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tevékenység</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kockázat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Részletek</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {securityActivity.slice(0, 20).map((activity) => (
                        <tr key={activity.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(activity.timestamp)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {activity.details?.username || `User ${activity.userId}`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                            {activity.activityType.replace(/_/g, ' ')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              activity.riskLevel >= 8 
                                ? 'bg-red-100 text-red-800' 
                                : activity.riskLevel >= 5
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {activity.riskLevel}/10
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                            {activity.details?.fileName || activity.details?.keyPressed || activity.details?.action || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports; 